import { randomFraction } from '../platform';

/**
 * Close codes the server uses to say "do not come back with this credential".
 * Everything else is treated as a transient condition worth another attempt.
 */
const NON_RETRYABLE_CLOSE_CODES = new Set([4400, 4401, 4403, 4409]);

export function shouldReconnect(closeCode: number): boolean {
  return !NON_RETRYABLE_CLOSE_CODES.has(closeCode);
}

export interface BackoffOptions {
  baseMs?: number;
  capMs?: number;
  random?: () => number;
}

/**
 * Delay before reconnection attempt number `attempt`, with full jitter.
 *
 * The jitter matters more than the growth: after a server restart every client
 * that was connected wakes up at the same instant, and a fixed schedule would
 * have them all reconnect together and knock it over again. Spreading each
 * client uniformly across its window turns that spike into a ramp.
 *
 * Ceilings grow 0.5s, 1s, 2s, 4s … up to 30s.
 */
export function reconnectDelay(attempt: number, options: BackoffOptions = {}): number {
  const baseMs = options.baseMs ?? 500;
  const capMs = options.capMs ?? 30_000;
  const ceiling = Math.min(capMs, baseMs * 2 ** Math.min(Math.max(attempt, 0), 10));
  return Math.floor(secureFraction(options.random) * ceiling);
}

function secureFraction(custom?: () => number): number {
  return custom ? custom() : randomFraction();
}
